import React, { useState, useEffect, useCallback } from 'react';
import { getData, updateData } from '../../services/seoXmlSitemapServices';
import { Button, Form } from 'react-bootstrap';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';

const Seoxmlsitemap = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    id: null,
    uploadedfile: null,   // XML sitemap file (maps to xml_file)
    uploadedfile1: null,  // LLMS file (maps to llms_file)
    xml_file: null,       // existing xml file name/path from backend
    llms_file: null       // existing llms file name/path from backend
  });

  const cms_sub_domain = localStorage.getItem('cms_sub_domain');

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData();
      if (response.length > 0) {
        const record = response[0];
        // Map backend columns (xml_file / llms_file) to form fields
        // (uploadedfile / uploadedfile1) so existing files show up
        // as "View Current File" links and don't wrongly fail validation.
        setFormData({
          id: record.id,
          uploadedfile: record.xml_file || null,
          uploadedfile1: record.llms_file || null,
          xml_file: record.xml_file || null,
          llms_file: record.llms_file || null,
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Validate form data (checks BOTH XML sitemap and LLMS files)
  const validateForm = () => {
    const newErrors = {};

    if (!formData.uploadedfile) {
      newErrors.uploadedfile = "Upload XML File is required";
    }
    if (!formData.uploadedfile1) {
      newErrors.uploadedfile1 = "Upload LLMS File is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    try {
      const { name, value, files } = e.target;
      const file = files?.[0];

      let updatedData = { ...formData };
      let updatedErrors = { ...errors };

      if (file) {
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.split('.').pop();
        const expectedExtension = name === 'uploadedfile1' ? 'txt' : 'xml';
        const expectedFileName = name === 'uploadedfile1' ? 'llms.txt' : 'sitemap.xml';

        if (fileExtension !== expectedExtension) {
          updatedErrors[name] = `Only .${expectedExtension} files are allowed!`;
        } else if (fileName !== expectedFileName) {
          updatedErrors[name] = `File must be named '${expectedFileName}'`;
        } else {
          updatedData[name] = file;
          updatedErrors[name] = '';
        }
      } else {
        updatedData[name] = value;
        if (value) {
          updatedErrors[name] = '';
        }
      }

      setFormData(updatedData);
      setErrors(updatedErrors);
    } catch (error) {
      console.error("Error in handleChange:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      const form = new FormData();
      form.append('id', formData.id);
      form.append('uploadedfile', formData.uploadedfile);
      form.append('uploadedfile1', formData.uploadedfile1);

      const result = await updateData(form);
      if (result.status === 200) {
        toast.success("Updated Successfully!", { position: 'top-right', autoClose: 3000 });
        fetchData(); // Refresh data after update
      } else {
        toast.error(result.message || "Failed to update data. Please try again.", { position: 'top-right', autoClose: 3000 });
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("Something went wrong. Please try again.", { position: 'top-right', autoClose: 3000 });
    }
  };

  // Build the public URL for a given filename (sitemap.xml or llms.txt)
  const buildFileUrl = (fileName) => {
    if (cms_sub_domain) {
      return `https://${cms_sub_domain}.scientexconference.com/${fileName}`;
    }
    if (window.location.hostname === "localhost") {
      return `http://localhost:3022/${fileName}`;
    }
    return `/${fileName}`;
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className='mb-3 fs-4'>SEO Config <b>/ XML Sitemap</b></p>
                </div>
                <div className="col-4 text-end" />
              </div>

              <div className="card p-3 mb-4">
                <Form onSubmit={handleSubmit}>
                  <div className="row">

                    {/* --- XML Sitemap upload --- */}
                    <Form.Group className="col-md-3 col-sm-6 mb-3">
                      <Form.Label className='form-label-custom'>Upload XML File <small className='text-danger'>*</small></Form.Label>
                      <Form.Control
                        type="file"
                        name="uploadedfile"
                        accept=".xml"
                        onChange={handleChange}
                        isInvalid={!!errors.uploadedfile}
                      />
                      <Form.Control.Feedback type="invalid">
                        <i className="mdi mdi-alert-circle-outline me-1"></i>
                        {errors.uploadedfile}
                      </Form.Control.Feedback>
                      {formData.uploadedfile && typeof formData.uploadedfile === 'string' && (
                        <p className="mb-0 mt-1">
                          <a href={formData.uploadedfile} target="_blank" rel="noopener noreferrer">
                            <i className="mdi mdi-open-in-new me-1"></i>View Current File
                          </a>
                        </p>
                      )}
                    </Form.Group>

                    {/* --- LLMS.txt upload --- */}
                    <Form.Group className="col-md-3 col-sm-6 mb-3">
                      <Form.Label className='form-label-custom'>Upload LLMS File <small className='text-danger'>*</small></Form.Label>
                      <Form.Control
                        type="file"
                        name="uploadedfile1"
                        accept=".txt"
                        onChange={handleChange}
                        isInvalid={!!errors.uploadedfile1}
                      />
                      <Form.Control.Feedback type="invalid">
                        <i className="mdi mdi-alert-circle-outline me-1"></i>
                        {errors.uploadedfile1}
                      </Form.Control.Feedback>
                      {formData.uploadedfile1 && typeof formData.uploadedfile1 === 'string' && (
                        <p className="mb-0 mt-1">
                          <a href={formData.uploadedfile1} target="_blank" rel="noopener noreferrer">
                            <i className="mdi mdi-open-in-new me-1"></i>View Current File
                          </a>
                        </p>
                      )}
                    </Form.Group>

                    {/* --- Instructions for both files --- */}
                    <Form.Group className="col-md-3 col-sm-6">
                      <Form.Label className='form-label-custom'>Instructions</Form.Label><br />
                      <small className='text-info d-block'>1. XML file name must be 'sitemap.xml'</small>
                      <small className='text-info d-block'>2. XML file format only ('.xml')</small>
                      <small className='text-info d-block'>3. LLMS file name must be 'llms.txt'</small>
                      <small className='text-info d-block'>4. LLMS file format only ('.txt')</small>
                    </Form.Group>

                    {/* --- Open current files in new tab --- */}
                    <Form.Group className="col-md-3 col-sm-6">
                      <Form.Label className='form-label-custom'>Check Files &nbsp;</Form.Label>

                      <div className="d-flex flex-column gap-2">
                        {formData.xml_file ? (
                          
                            <a className="btn btn-warning text-dark"
                            href={buildFileUrl('sitemap.xml')}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="mdi mdi-open-in-new me-1"></i>Open sitemap.xml
                          </a>
                        ) : (
                          <p className="mb-0">
                            <i className="mdi mdi-alert-circle-outline me-1 text-muted"></i>
                            No XML file available
                          </p>
                        )}

                        {formData.llms_file ? (
                          
                            <a className="btn btn-warning text-dark"
                            href={buildFileUrl('llms.txt')}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="mdi mdi-open-in-new me-1"></i>Open llms.txt
                          </a>
                        ) : (
                          <p className="mb-0">
                            <i className="mdi mdi-alert-circle-outline me-1 text-muted"></i>
                            No LLMS file available
                          </p>
                        )}
                      </div>
                    </Form.Group>

                  </div>
                  <Button variant="success" type="submit">Submit</Button>
                </Form>
              </div>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Seoxmlsitemap;